/**
 * screens/CampaignsScreen.tsx — bulk promotional/marketing calls.
 *
 * Faithful to the prototype: intro + New campaign; campaign cards with a
 * class chip, reason/created/total, a big % progress figure, a SEGMENTED
 * progress bar (connected/queued/suppressed/failed) and a stats row with
 * colored dots; a detail drawer listing every contact + per-contact status;
 * and a New-campaign modal (name, reason pills, paste-contacts w/ live count).
 * Launch → intents; promo-no-consent / DND contacts auto-suppressed.
 *
 * Wired to /v1/campaigns (+ /:id, + /:id/launch).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, ActivityIndicator, Pressable, ScrollView, TextInput } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useConsole } from '../context/voiceConsoleContext';
import { Card, Chip, Button, ScreenBody, Modal, OVERLAY_POSITION } from '../components/voiceUi';
import { listCampaigns, getCampaign, listOutboundReasons, createCampaign, launchCampaign } from '../services/voice/voiceService';
import { showSuccess } from '../utils/successHandler';
import { handleApiError } from '../utils/errorHandler';

const OK = '#3FA46A'; const INFO = '#2D5CF6'; const WARN = '#C98A2B'; const BAD = '#D65A4E'; const MUTE = '#a6a6ac';

// derive segment counts from contacts (consentResult + intentId presence)
function segmentsOf(contacts: any[]) {
  const total = contacts.length || 1;
  const connected = contacts.filter((c) => c.intentId && (c.consentResult === 'granted' || c.consentResult === 'not_required')).length;
  const suppressed = contacts.filter((c) => !c.intentId).length;
  const queued = contacts.filter((c) => c.intentId).length - connected;
  const failed = 0;
  return { total, connected, queued: Math.max(0, queued), suppressed, failed };
}

export default function CampaignsScreen() {
  const { theme } = useTheme(); const c = theme.colors;
  const { role, orgId } = useConsole();
  const canCreate = role !== 'viewer';

  const [rows, setRows] = useState<any[]>([]);
  const [reasons, setReasons] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [selId, setSelId] = useState<string | null>(null);
  const [detail, setDetail] = useState<any | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [cs, rs] = await Promise.all([listCampaigns(), listOutboundReasons()]);
    setRows(cs.data); setReasons(rs.data); setLoading(false);
  }, []);
  useEffect(() => { void load(); }, [load, orgId]);

  const openDetail = async (id: string) => { setSelId(id); setDetail(null); setDetail(await getCampaign(id)); };

  const launch = async (id: string) => {
    try { const r = await launchCampaign(id); showSuccess(null, `Launched · ${r?.dialed ?? 0} dialing, ${r?.suppressed ?? 0} suppressed`); void load(); }
    catch (e) { handleApiError(e, 'Launch failed'); }
  };

  return (
    <ScreenBody maxWidth={1040}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14, marginBottom: 18, flexWrap: 'wrap' }}>
        <Text style={{ flex: 1, minWidth: 280, maxWidth: 660, fontSize: 12.5, color: c.ink3, lineHeight: 19 }}>
          Upload a list of contacts and let SK Voice run promotional or marketing calls. Every contact still clears consent, quiet-hours and frequency-cap checks — contacts without consent are suppressed automatically, never dialed.
        </Text>
        {canCreate && <Button kind="primary" label="+ New campaign" onPress={() => setOpenNew(true)} />}
      </View>

      {loading ? <ActivityIndicator color={c.ink3} />
        : rows.length === 0 ? (
          <View style={{ padding: 56, alignItems: 'center', borderWidth: 1, borderColor: c.lineStrong, borderRadius: 12, backgroundColor: c.paper2, borderStyle: 'dashed' }}>
            <Text style={{ fontWeight: '600', fontSize: 14, color: c.ink }}>No campaigns yet</Text>
            <Text style={{ fontSize: 12.5, color: c.ink3, maxWidth: 420, marginTop: 6, textAlign: 'center', lineHeight: 19 }}>
              Start one with New campaign — upload contacts and SK Voice dials the consented ones.
            </Text>
          </View>
        ) : (
          <View style={{ gap: 14 }}>
            {rows.map((cp) => <CampaignCard key={cp.id} cp={cp} canCreate={canCreate} onOpen={() => openDetail(cp.id)} onLaunch={() => launch(cp.id)} />)}
          </View>
        )}

      <CampaignDrawer id={selId} detail={detail} onClose={() => { setSelId(null); setDetail(null); }} />
      <NewCampaignModal open={openNew} reasons={reasons} onClose={() => setOpenNew(false)} onCreated={() => { setOpenNew(false); void load(); }} />
    </ScreenBody>
  );
}

// A colored legend dot + "N Label" — shared by card + drawer.
function LegendDot({ n, label, color }: { n: number; label: string; color: string }) {
  const { theme } = useTheme(); const c = theme.colors;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
      <View style={{ width: 8, height: 8, borderRadius: 999, backgroundColor: color }} />
      <Text style={{ fontSize: 12, color: c.ink2 }}>{n} {label}</Text>
    </View>
  );
}

// The 4-segment progress bar (connected/queued/suppressed/failed) over its track.
function SegBar({ seg }: { seg: ReturnType<typeof segmentsOf> }) {
  const { theme } = useTheme(); const c = theme.colors;
  const parts = [
    { n: seg.connected, color: OK }, { n: seg.queued, color: INFO },
    { n: seg.suppressed, color: BAD }, { n: seg.failed, color: WARN },
  ];
  return (
    <View style={{ height: 8, borderRadius: 20, overflow: 'hidden', backgroundColor: c.paper3 ?? c.paper2, flexDirection: 'row', width: '100%' }}>
      {parts.map((b, i) => b.n > 0 ? <View key={i} style={{ flex: b.n, backgroundColor: b.color }} /> : null)}
    </View>
  );
}

function CampaignCard({ cp, canCreate, onOpen, onLaunch }: { cp: any; canCreate: boolean; onOpen: () => void; onLaunch: () => void }) {
  const { theme } = useTheme(); const c = theme.colors;
  // List payload now carries stats + regulatoryClass (backend enriches the list).
  const seg = cp.stats ? { total: cp.stats.total || 1, connected: cp.stats.connected, queued: cp.stats.pending, suppressed: cp.stats.suppressed, failed: cp.stats.failed } : segmentsOf([]);
  const done = seg.connected + seg.suppressed + seg.failed;
  const pct = seg.total > 0 ? Math.round((done / seg.total) * 100) : 0;
  const isPromo = cp.regulatoryClass === 'promotional';
  const when = cp.launchedAt ? new Date(cp.launchedAt).toLocaleDateString() : null;
  return (
    <Pressable onPress={onOpen} style={{ backgroundColor: c.card, borderWidth: 1, borderColor: c.line, borderRadius: 12, padding: 18 }}>
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
        <View style={{ flex: 1, minWidth: 0 }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 9, marginBottom: 4 }}>
            <Text style={{ fontWeight: '600', fontSize: 14, color: c.ink }}>{cp.name}</Text>
            {cp.regulatoryClass && <Chip kind="outline">{cp.regulatoryClass}</Chip>}
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Text style={{ fontFamily: theme.font.mono, fontSize: 11.5, color: c.ink3 }}>{cp.reasonCode}</Text>
            {when && <Text style={{ fontSize: 11, color: c.ink4 }}>· {when}</Text>}
            <Text style={{ fontSize: 11, color: c.ink4 }}>· {seg.total} contacts</Text>
          </View>
        </View>
        {canCreate && cp.status === 'draft'
          ? <Button kind="primary" label="Launch" onPress={onLaunch} />
          : <Text style={{ fontSize: 22, fontWeight: '700', color: c.ink }}>{pct}%</Text>}
      </View>
      <SegBar seg={seg} />
      <View style={{ flexDirection: 'row', gap: 16, flexWrap: 'wrap', marginTop: 10 }}>
        <LegendDot n={seg.connected} label="Connected" color={OK} />
        <LegendDot n={seg.queued} label="Queued" color={INFO} />
        <LegendDot n={seg.suppressed} label="Suppressed" color={BAD} />
        <LegendDot n={seg.failed} label="Failed" color={WARN} />
      </View>
    </Pressable>
  );
}

function CampaignDrawer({ id, detail, onClose }: { id: string | null; detail: any; onClose: () => void }) {
  const { theme } = useTheme(); const c = theme.colors;
  if (!id) return null;
  const campaign = detail?.campaign;
  const contacts: any[] = detail?.contacts ?? [];
  const st = detail?.stats;
  // Prefer the backend's live counters; fall back to deriving from contacts.
  const seg = st
    ? { total: st.total || contacts.length || 1, connected: st.connected, queued: st.pending, suppressed: st.suppressed, failed: st.failed }
    : segmentsOf(contacts);
  return (
    <>
      <Pressable onPress={onClose} style={{ position: OVERLAY_POSITION, top: 0, bottom: 0, left: 0, right: 0, backgroundColor: 'rgba(0,0,0,0.25)', zIndex: 49 } as any} />
      <View style={{ position: OVERLAY_POSITION, top: 0, bottom: 0, right: 0, width: 480, maxWidth: '100%', backgroundColor: c.card, borderLeftWidth: 1, borderLeftColor: c.line, zIndex: 50 } as any}>
        <View style={{ flexDirection: 'row', alignItems: 'center', padding: 18, borderBottomWidth: 1, borderBottomColor: c.line }}>
          <Text style={{ flex: 1, fontSize: 16, fontWeight: '700', color: c.ink }}>{campaign?.name ?? 'Campaign'}</Text>
          <Pressable onPress={onClose}><Text style={{ fontSize: 20, color: c.ink3 }}>×</Text></Pressable>
        </View>
        {!detail ? <View style={{ padding: 20 }}><ActivityIndicator color={c.ink3} /></View> : (
          <ScrollView style={{ padding: 18 }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <Text style={{ fontFamily: theme.font.mono, fontSize: 12, color: c.ink3 }}>{campaign?.reasonCode}</Text>
              {campaign?.launchedAt && <Text style={{ fontSize: 11, color: c.ink4 }}>· {new Date(campaign.launchedAt).toLocaleDateString()}</Text>}
              <Text style={{ fontSize: 11, color: c.ink4 }}>· {seg.total} contacts</Text>
            </View>
            <SegBar seg={seg} />
            <View style={{ flexDirection: 'row', gap: 16, flexWrap: 'wrap', marginTop: 10, marginBottom: 18 }}>
              <LegendDot n={seg.connected} label="Connected" color={OK} />
              <LegendDot n={seg.queued} label="Queued" color={INFO} />
              <LegendDot n={seg.suppressed} label="Suppressed" color={BAD} />
              <LegendDot n={seg.failed} label="Failed" color={WARN} />
            </View>
            {/* table header */}
            <View style={{ flexDirection: 'row', paddingBottom: 8, borderBottomWidth: 1, borderBottomColor: c.line }}>
              <Text style={{ flex: 1, fontSize: 10.5, fontWeight: '600', letterSpacing: 0.6, textTransform: 'uppercase', color: c.ink4 }}>Contact</Text>
              <Text style={{ width: 90, fontSize: 10.5, fontWeight: '600', letterSpacing: 0.6, textTransform: 'uppercase', color: c.ink4 }}>Consent</Text>
              <Text style={{ width: 100, fontSize: 10.5, fontWeight: '600', letterSpacing: 0.6, textTransform: 'uppercase', color: c.ink4 }}>Status</Text>
            </View>
            {contacts.map((ct) => <ContactRow key={ct.id} ct={ct} />)}
            {contacts.length === 0 && <Text style={{ fontSize: 12, color: c.ink4, paddingTop: 14 }}>No contacts imported.</Text>}
          </ScrollView>
        )}
      </View>
    </>
  );
}

// One row of the contact table: avatar + name/phone, consent, status pill.
function ContactRow({ ct }: { ct: any }) {
  const { theme } = useTheme(); const c = theme.colors;
  const label = ct.contactName || ct.name || ct.phone || ct.customerId || '—';
  const initials = String(label).replace(/^\+?\d/, '').trim().split(/\s+/).map((w: string) => w[0]).join('').slice(0, 2).toUpperCase() || '#';
  // Status: no intent → Suppressed (never dialed); else derive from consent snapshot.
  const status = !ct.intentId ? { t: 'Suppressed', col: BAD }
    : ct.consentResult === 'granted' || ct.consentResult === 'not_required' ? { t: 'Queued', col: INFO }
    : { t: 'Pending', col: MUTE };
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', paddingVertical: 11, borderBottomWidth: 1, borderBottomColor: c.line }}>
      <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 10, minWidth: 0 }}>
        <View style={{ width: 28, height: 28, borderRadius: 999, backgroundColor: c.paper3 ?? c.paper2, alignItems: 'center', justifyContent: 'center' }}>
          <Text style={{ fontSize: 10, fontWeight: '600', color: c.ink3 }}>{initials}</Text>
        </View>
        <Text style={{ fontFamily: theme.font.mono, fontSize: 12, color: c.ink2 }} numberOfLines={1}>{ct.phone ?? label}</Text>
      </View>
      <Text style={{ width: 90, fontSize: 12, color: c.ink3 }}>{ct.consentResult ?? '—'}</Text>
      <View style={{ width: 100, flexDirection: 'row', alignItems: 'center', gap: 6 }}>
        <View style={{ width: 7, height: 7, borderRadius: 999, backgroundColor: status.col }} />
        <Text style={{ fontSize: 12, color: c.ink2 }}>{status.t}</Text>
      </View>
    </View>
  );
}

function NewCampaignModal({ open, reasons, onClose, onCreated }: { open: boolean; reasons: any[]; onClose: () => void; onCreated: () => void }) {
  const { theme } = useTheme(); const c = theme.colors;
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  // Parse "Name, +phone" or just "phone" per line → {name?, phone}. The phone is
  // the last comma field (or the whole line); name is the rest. Normalization to
  // E.164 happens server-side.
  const parseContacts = (raw: string) => raw.split('\n').map((l) => l.trim()).filter(Boolean).map((line) => {
    const parts = line.split(',').map((p) => p.trim()).filter(Boolean);
    const phone = (parts.length > 1 ? parts[parts.length - 1] : parts[0]) ?? '';
    const nm = parts.length > 1 ? parts.slice(0, -1).join(' ') : undefined;
    return nm ? { phone, name: nm } : { phone };
  }).filter((ct) => ct.phone);
  const count = parseContacts(text).length;
  const reset = () => { setName(''); setReason(''); setText(''); };

  // CSV import → fills the same textarea, so paste + upload share one parse path
  // and the live count. Drops a header row if the first line has no digits (a
  // "name,phone" header vs a real number). Appends to any pasted lines.
  const fileRef = useRef<HTMLInputElement | null>(null);
  const onCsv = (file: File | undefined) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const raw = String(reader.result ?? '');
      const lines = raw.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
      if (lines.length && !/\d/.test(lines[0])) lines.shift(); // strip header row
      setText((prev) => [prev.trim(), lines.join('\n')].filter(Boolean).join('\n'));
    };
    reader.readAsText(file);
  };

  // A reference CSV the operator can fill in and re-upload. Built client-side.
  const downloadSample = () => {
    const csv = 'name,phone\nPriya Menon,+91 98451 27760\nRahul Iyer,9843044129\n';
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = 'sk-voice-campaign-sample.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Create AND launch in one action (matches the "Launch campaign" button): a
  // draft nobody launches never dials, so the intuitive flow launches immediately.
  const create = async () => {
    setBusy(true);
    try {
      const contacts = parseContacts(text);
      const created = await createCampaign({ name, reason_code: reason, contacts });
      const id = (created as any)?.campaignId ?? (created as any)?.data?.campaignId;
      if (id) await launchCampaign(id);
      showSuccess(null, `Launched · ${contacts.length} contacts`); reset(); onCreated();
    } catch (e) { handleApiError(e, 'Launch failed'); } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={() => { reset(); onClose(); }} width={500}>
      <Text style={{ fontSize: 16, fontWeight: '700', color: c.ink, marginBottom: 4 }}>New campaign</Text>
      <Text style={{ fontSize: 12, color: c.ink3, marginBottom: 12 }}>Bulk-dial a contact list. Consent still gates every promotional call.</Text>
      <Text style={{ fontSize: 12, color: c.ink2, marginBottom: 4 }}>Name</Text>
      <TextInput value={name} onChangeText={setName} placeholderTextColor={c.ink4}
        style={{ height: 36, paddingHorizontal: 12, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 7, color: c.ink, backgroundColor: c.card, fontSize: 13 }} />
      <Text style={{ fontSize: 12, color: c.ink2, marginTop: 12, marginBottom: 6 }}>Reason to call</Text>
      <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
        {reasons.map((rc) => {
          const sel = reason === rc.reasonCode;
          return (
            <Pressable key={rc.reasonCode} onPress={() => setReason(rc.reasonCode)}
              style={{ paddingVertical: 8, paddingHorizontal: 11, borderRadius: 8, borderWidth: 1, borderColor: sel ? c.ink : c.lineStrong, backgroundColor: sel ? c.card : 'transparent' }}>
              <Text style={{ fontFamily: theme.font.mono, fontSize: 12, fontWeight: '600', color: c.ink }}>{rc.reasonCode}</Text>
              <Text style={{ fontSize: 10.5, color: rc.regulatoryClass === 'promotional' ? WARN : c.ink4, marginTop: 1 }}>{rc.regulatoryClass ?? ''}</Text>
            </Pressable>
          );
        })}
      </View>
      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, marginBottom: 4 }}>
        <Text style={{ fontSize: 12, color: c.ink2 }}>Contacts</Text>
        <View style={{ flexDirection: 'row', gap: 14 }}>
          <Pressable onPress={() => fileRef.current?.click()}>
            <Text style={{ fontSize: 11.5, fontWeight: '600', color: c.ink }}>＋ Import CSV</Text>
          </Pressable>
          <Pressable onPress={downloadSample}>
            <Text style={{ fontSize: 11.5, color: c.ink3 }}>↓ Sample CSV</Text>
          </Pressable>
        </View>
      </View>
      <input ref={fileRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => { onCsv(e.target.files?.[0]); e.target.value = ''; }} />
      <TextInput value={text} onChangeText={setText} multiline placeholder={'One per line:  Priya Menon, +91 98451 27760'} placeholderTextColor={c.ink4}
        style={{ minHeight: 120, paddingHorizontal: 12, paddingVertical: 8, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 7, color: c.ink, backgroundColor: c.card, fontFamily: theme.font.mono, fontSize: 13, textAlignVertical: 'top' }} />
      <Text style={{ fontSize: 11, color: c.ink4, marginTop: 8 }}>Paste or import a CSV (name, phone per line). {count} contacts detected.</Text>
      <View style={{ marginTop: 14 }}>
        <Button kind="primary" label={busy ? 'Launching…' : 'Launch campaign'} onPress={create} disabled={!name || !reason || count === 0 || busy} />
      </View>
      <Text style={{ fontSize: 11, color: c.ink4, marginTop: 10, textAlign: 'center' }}>Contacts without promotional consent are created as suppressed — they are never dialed.</Text>
    </Modal>
  );
}
